import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router'
import { FaTrash } from 'react-icons/fa'
import { Icon, Stack, Alert, FormControl, Button, Input, DateChooser, Textarea, Heading, Text, Box, Loading, Select, UserChooser, EmptyBox, ConfirmButton, SimpleModal, UserChip, Kanban, EditableContent } from '@/components/lib'
import useStore from '../../store'
import utils from '@/utils/utils'
import api from '../../api'
import useTitle from '../../hooks/useTitle'

import KanbanImage from '../../images/kanban.png'

/*
 * For the Kanban board we maintain two states: lanes and laneTasks.
 * The former is a list of the current lanes (plain as)
 * The latter is a obj where each key is the lane ID and the value is an array of tasks for that lane
 * When updating tasks, we only update the laneTasks object
 * When updating lanes, we update both the lanes (so the new lane is rendered) and the laneTasks (so the tasks can be added to the new lane)
 */

export default function Tasks () {
  const [lanes, setLanes] = useState([])
  const [laneTasks, setLaneTasks] = useState({})
  const [loading, setLoading] = useState(false)
  const { trial } = useStore()
  const trialId = trial?._id
  useTitle('Tasks', trial)

  useEffect(() => {
    if (!trialId) return
    setLoading(true)
    api.taskLanes.getForTrial(trialId, laneData => {
      api.tasks.getForTrial(trialId, taskData => {
        const newLaneTasks = {}
        laneData?.lanes?.forEach(lane => {
          newLaneTasks[lane._id] = taskData?.tasks?.filter(t => t.taskLane === lane._id)
        })
        setLanes(laneData.lanes)
        setLaneTasks(newLaneTasks)
        setLoading(false)
      })
    })
  }, [trialId])

  function createLane () {
    api.taskLanes.create(trialId, { name: 'New lane' }, lane => {
      const newLanes = Object.assign([], lanes)
      newLanes.push(lane)
      setLanes(newLanes)
      const newLaneTasks = Object.assign({}, laneTasks)
      newLaneTasks[lane._id] = []
      setLaneTasks(newLaneTasks)
    })
  }
  function editLane (laneId, data) {
    api.taskLanes.update(laneId, data, lane => {
      const newLanes = lanes.map(l => {
        if (l._id === laneId) return lane
        const newLane = { ...l }
        if (lane.isCompleteLane) newLane.isCompleteLane = false
        return newLane
      })
      setLanes(newLanes)
    })
  }
  function deleteLane (laneId) {
    api.taskLanes.delete(laneId, () => {
      const newLanes = lanes.filter(l => l._id !== laneId)
      setLanes(newLanes)
      const newLaneTasks = Object.assign({}, laneTasks)
      delete newLaneTasks[laneId]
      setLaneTasks(newLaneTasks)
    }, err => utils.toast('error', 'Unable to delete lane', err.message))
  }

  function addTask (laneId, data) {
    api.tasks.create(trialId, { taskLane: laneId, title: 'New task', ...data }, task => {
      const newLaneTasks = Object.assign({}, laneTasks)
      newLaneTasks[laneId]?.splice(task.index || 0, 0, task)
      setLaneTasks(newLaneTasks)
    })
  }
  function editTask (taskId, laneId, data) {
    api.tasks.update(taskId, { taskLane: laneId, ...data }, task => {
      setLaneTasks((prev) => {
        const newLaneTasks = Object.assign({}, prev)
        newLaneTasks[laneId] = newLaneTasks[laneId].map(t => t._id === taskId ? task : t)
        return newLaneTasks
      })
    })
  }
  function deleteTask (taskId) {
    api.tasks.delete(taskId, () => {
      const newLaneTasks = Object.assign({}, laneTasks)
      Object.keys(newLaneTasks).forEach(laneId => {
        newLaneTasks[laneId] = newLaneTasks[laneId].filter(t => t._id !== taskId)
      })
      setLaneTasks(newLaneTasks)
    })
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h2' size='lg'>Tasks</Heading>
        <Text>Coordinate and manage tasks relating to this trial.</Text>
      </Box>

      {loading && <Box textAlign='center'><Loading /></Box>}

      {lanes.length === 0 && !loading && (
        <EmptyBox
          image={KanbanImage}
          title='Your task manager has no lanes'
          description='Create lanes to start adding and managing your tasks.'
        >
          <Button colorPalette='primary' onClick={createLane}>Add a lane</Button>
        </EmptyBox>
      )}
      {lanes.length > 0 && (
        <Box minH='400px' w='100%' overflowX='scroll' pb={10}>
          <Kanban
            containers={lanes}
            items={laneTasks}
            setContainers={setLanes}
            setItems={setLaneTasks}
            onAddContainer={createLane}
            onEditContainer={editLane}
            onDeleteContainer={deleteLane}
            onAddItem={addTask}
            onEditItem={editTask}
            onDeleteItem={deleteTask}
            ItemEditor={TaskEditor}
          />
        </Box>)}
    </Stack>
  )
}

function TaskEditor ({ isOpen, onClose, task, onSave, onDelete }) {
  const [title, setTitle] = useState(task.title)
  const [description, setDescription] = useState(task.description)
  const [assignees, setAssignees] = useState(task.assignees || [])
  const { getTrialUsers } = useStore()
  const availableAssignees = useMemo(() => getTrialUsers(task?.trial), [task?.trial])

  function updateAssignees (assigneeUpdate) {
    const newAssignees = [...assigneeUpdate]
    setAssignees(newAssignees)
    onSave({ assignees: newAssignees })
  }
  function removeAssignee (assigneeId) {
    const newAssignees = assignees.filter(a => a !== assigneeId)
    setAssignees(newAssignees)
    onSave({ assignees: newAssignees })
  }

  return (
    <SimpleModal
      size='xl'
      isOpen={isOpen} onClose={e => onClose()}
      header=''
      body={
        <Stack onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
          <Stack gap={5} direction='row'>
            <Stack gap={3} flex={1}>
              <EditableContent
                initialValue={title} onSave={v => {
                  onSave({ title: v })
                  setTitle(v)
                }} InputElement={Input} DisplayElement={Heading} displayProps={{ size: 'md' }} placeholder='Add a title...'
              />
              <Box>
                <EditableContent
                  multiLine initialValue={description} onSave={v => {
                    onSave({ description: v })
                    setDescription(v)
                  }} InputElement={Textarea} DisplayElement={Text} displayProps={{ whiteSpace: 'pre-wrap' }} placeholder='Add a description...'
                />
              </Box>

              {task.queryQuery &&
                <Alert status='info' title='This task is linked to a query'>
                  <Stack>
                    <Text>Trialflare may track and manage the lifecycle of this task for you.</Text>
                    <Box>
                      <Button as={Link} to={`/trials/${task.queryQuery.trial}/queries`} size='sm' colorPalette='blue'>Manage queries</Button>
                    </Box>
                  </Stack>
                </Alert>}

              {task.siteMonitoringIssueSiteMonitoringIssue &&
                <Alert status='info' title='This task is linked to a site monitoring issue'>
                  <Stack>
                    <Text>Trialflare may track and manage the lifecycle of this task for you.</Text>
                    <Box>
                      <Button as={Link} to={`/trials/${task.siteMonitoringIssueSiteMonitoringIssue.trial}/sites/${task.siteMonitoringIssueSiteMonitoringIssue.site}/monitoring`} size='sm'>Manage site monitoring issues</Button>
                    </Box>
                  </Stack>
                </Alert>}
            </Stack>

            <Stack gap={3} w='250px' p={3} bg='bg.canvas' rounded='md' onPointerDown={e => e.stopPropagation()}>
              <Text color='fg.muted' fontSize='sm' fontWeight='bold'>Created {utils.formatDateTime(task.createdAt)}</Text>
              {task.createdByUser && <UserChip user={task.createdByUser} compact />}

              <FormControl label='Status'>
                <Select size='sm' value={task.status} onChange={e => onSave({ status: e.target.value })}>
                  <option value='pending'>Pending</option>
                  <option value='completed'>Completed</option>
                </Select>
              </FormControl>

              <FormControl label='Due date'>
                <Stack direction='row' alignItems='center'>
                  <DateChooser placeholder='Set a due date...' size='sm' value={task.dueDate} onChange={date => onSave({ dueDate: date })} />
                  <Button size='sm' onClick={e => onSave({ dueDate: null })}>Clear</Button>
                </Stack>
              </FormControl>

              <FormControl label='Assignees'>
                <Stack w='100%'>
                  <UserChooser
                    size='sm' onChosen={updateAssignees} multiple
                    selectedUsers={assignees}
                    users={availableAssignees}
                  />
                  {task.assigneeUsers?.map(a => (
                    <Stack direction='row' key={a._id} alignItems='center' justifyContent='space-between'>
                      <UserChip user={a} onDelete={() => setAssignees(assignees.filter(u => u._id !== a._id))} compact />
                      <ConfirmButton
                        trigger={<Button size='xs' variant='ghost'><Icon as={FaTrash} /></Button>}
                        header='Really remove this assignee?'
                        onConfirm={() => removeAssignee(a._id)}
                      />
                    </Stack>
                  ))}
                </Stack>
              </FormControl>

              <ConfirmButton
                trigger={<Button size='sm' variant='ghost' w='100%' colorPalette='red'>Delete task</Button>}
                header='Really delete this task?'
                onConfirm={onDelete}
              />
            </Stack>
          </Stack>
        </Stack>
      }
      footer={
        <Box display='flex' justifyContent='end'>
          <Button
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              onClose()
            }} colorPalette='primary'
          >Close
          </Button>
        </Box>
      }
    />
  )
}
